import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import PrivilegeService from "../../services/PrivilegeService";

export const fetchAccessPrivilegesByEmployee = createAsyncThunk(
    "privilege/fetchAccessPrivilegesByEmployee",
    async (userId, { rejectWithValue }) => {
        try {
            const response = await PrivilegeService.GetAccessPrivilegesByEmployee(userId);
            const data = response.data;

            if (data && data.StatusCode && data.StatusCode !== 200) {
                return rejectWithValue(
                    data.Result || data.Message || "Failed to fetch access privileges"
                );
            }

            return data?.ResultSet || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Result ||
                error.response?.data?.Message || 
                error.message || 
                "Failed to fetch access privileges"
            );
        }
    }
)

export const updateAccessPrivileges = createAsyncThunk(
    "privilege/updateAccessPrivilege",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await PrivilegeService.UpdateAccessPrivileges(payload);
            const data = response.data;

            if (data && data.StatusCode && data.StatusCode !== 200) {
                return rejectWithValue(
                    data.Result || data.Message || "Failed to update access privileges"
                );
            }

            return data?.ResultSet || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Result ||
                error.response?.data?.Message || 
                error.message || 
                "Failed to update access privileges"
            );
        }
    }
)

export const addAccessPrivileges = createAsyncThunk(
    "privilege/addAccessPrivilege",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await PrivilegeService.AddAccessPrivileges(payload);
            const data = response.data;

            if (data && data.StatusCode && data.StatusCode !== 200) {
                return rejectWithValue(
                    data.Result || data.Message || "Failed to add access privilege"
                );
            }

            return data?.ResultSet || data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Result ||
                error.response?.data?.Message || 
                error.message || 
                "Failed to add access privilege"
            );
        }
    }
)

export const fetchSysApplications = createAsyncThunk(
    "privilege/fetchSysApplications",
    async (_, { rejectWithValue }) => {
        try {
            const response = await PrivilegeService.GetSysApplication();
            const data = response.data;

            if (data && data.StatusCode && data.StatusCode !== 200) {
                return rejectWithValue(
                    data.Result || data.Message || "Failed to fetch system applications"
                );
            }

            return data?.ResultSet || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Result ||
                error.response?.data?.Message || 
                error.message || 
                "Failed to fetch system applications"
            );
        }
    }
);

export const fetchPrivilegesBySystemId = createAsyncThunk(
    "privilege/fetchPrivilegesBySystemId",
    async (systemId, { rejectWithValue }) => {
        try {
            const response = await PrivilegeService.GetPrivilegesBySystemId(systemId);

            return response.data?.ResultSet || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message || 
                error.message || 
                "Failed to fetch system privileges"
            );
        }
    }
)

export const updateSystemPrivilege = createAsyncThunk(
    "privilege/updateSystemPrivilege",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await PrivilegeService.UpdateSystemPrivileges(payload);

            return response.data?.ResultSet || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message || 
                error.message || 
                "Failed to update system privileges"
            );
        }
    }
)

export const addSystemPrivilege = createAsyncThunk(
    "privilege/addSystemPrivilege",
    async (payload, { rejectWithValue }) => {
        try {
            const response = await PrivilegeService.AddSystemPrivileges(payload);

            return response.data?.ResultSet || [];
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.Message || 
                error.message || 
                "Failed to add system privileges"
            );
        }
    }
)

const initialState = {
    accessPrivileges: [],
    systemPrivileges: [],
    sysApplications: [],

    accessPrivilegesLoading: false,
    accessPrivilegesLoaded: false,

    systemPrivilegesLoading: false,
    sysApplicationsLoading: false,

    saving: false,

    error: null,
    success: false,
}

// ---- SLICE ----

const privilegeSlice = createSlice({
    name: "privilege",

    initialState,

    reducers: {
        clearPrivilegeError: (state) => {
            state.error = null;
        },

        clearPrivilegeSuccess: (state) => {
            state.success = false;
        },

        clearPrivileges: (state) => {
            state.accessPrivileges = [];
            state.systemPrivileges = [];
            state.sysApplications = [];
        },
    },

    extraReducers: (builder) => {
        builder

            // Fetch Access Privileges
            .addCase(
                fetchAccessPrivilegesByEmployee.pending,
                (state) => {
                    state.accessPrivilegesLoading = true;
                    state.accessPrivilegesLoaded = false;
                    state.error = null;
                }
            )
            .addCase(
                fetchAccessPrivilegesByEmployee.fulfilled,
                (state, action) => {
                    state.accessPrivilegesLoading = false;
                    state.accessPrivilegesLoaded = true;
                    state.accessPrivileges = action.payload;
                }
            )
            .addCase(
                fetchAccessPrivilegesByEmployee.rejected,
                (state, action) => {
                    state.accessPrivilegesLoading = false;
                    state.accessPrivilegesLoaded = false;
                    state.error = action.payload || "Failed to fetch access privileges";
                }
            )

            // Fetch System Applications
            .addCase(
                fetchSysApplications.pending,
                (state) => {
                    state.sysApplicationsLoading = true;
                    state.error = null;
                }
            )
            .addCase(
                fetchSysApplications.fulfilled,
                (state, action) => {
                    state.sysApplicationsLoading = false;
                    state.sysApplications = action.payload;
                }
            )
            .addCase(
                fetchSysApplications.rejected,
                (state, action) => {
                    state.sysApplicationsLoading = false;
                    state.error = action.payload || "Failed to fetch system applications";
                }
            )

            // Add Access Privilege

            .addCase(addAccessPrivileges.pending, (state) => {
                state.saving = true;
                state.error = null;
                state.success = false;
            })

            .addCase(addAccessPrivileges.fulfilled, (state) => {
                state.saving = false;
                state.success = true;
            })

            .addCase(addAccessPrivileges.rejected, (state, action) => {
                state.saving = false;
                state.error =
                action.payload || "Failed to add access privilege";
            })

            // Update Access Privilege
            .addCase(updateAccessPrivileges.pending, (state) => {
                state.saving = true;
                state.error = null;
                state.success = false;
            })

            .addCase(updateAccessPrivileges.fulfilled, (state) => {
                state.saving = false;
                state.success = true;
            })

            .addCase(updateAccessPrivileges.rejected, (state, action) => {
                state.saving = false;
                state.error =
                action.payload || "Failed to update access privilege";
            })

            // Fetch system privileges
            .addCase(
                fetchPrivilegesBySystemId.pending,
                (state) => {
                    state.systemPrivilegesLoading = true;
                    state.error = null;
                }
            )
            .addCase(
                fetchPrivilegesBySystemId.fulfilled,
                (state, action) => {
                    state.systemPrivilegesLoading = false;
                    state.systemPrivileges = action.payload;
                }
            )
            .addCase(
                fetchPrivilegesBySystemId.rejected,
                (state, action) => {
                    state.systemPrivilegesLoading = false;
                    state.error = action.payload || "Failed to fetch access privileges";
                }
            )

            // Add system privileges
            .addCase(addSystemPrivilege.pending, (state) => {
                state.saving = true;
                state.error = null;
                state.success = false;
            })

            .addCase(addSystemPrivilege.fulfilled, (state) => {
                state.saving = false;
                state.success = true;
            })

            .addCase(addSystemPrivilege.rejected, (state, action) => {
                state.saving = false;
                state.error =
                action.payload || "Failed to add system privilege";
            })

            // Update system privileges
            .addCase(updateSystemPrivilege.pending, (state) => {
                state.saving = true;
                state.error = null;
                state.success = false;
            })

            .addCase(updateSystemPrivilege.fulfilled, (state) => {
                state.saving = false;
                state.success = true;
            })

            .addCase(updateSystemPrivilege.rejected, (state, action) => {
                state.saving = false;
                state.error =
                action.payload || "Failed to update system privilege";
            })
    }
})

export const {
    clearPrivilegeError,
    clearPrivilegeSuccess,
    clearPrivileges
} = privilegeSlice.actions;

export default privilegeSlice.reducer;